import { useState, useEffect } from "react";

import {
  DashboardFilterType,
  GetDashboardProjectSummaryContent,
} from "@/types/dashboard";
import { ValueType } from "@/types/bid";

import { formatDollar, isNotEmpty, formatToPercentString } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import {
  DefaultDashbboardProjectSummary,
  DefaultDashboardProjectTargetAchievement,
  getDashboardProjectFilterToString,
} from "@/consts/Dashboard";

import {
  GetDashboardProjectSummary,
  GetTargetAchievement,
  SetExchangeRate,
} from "@/services/Dashboard";

import DashboardSummaryCard from "@/components/DashboardSummaryCard";
import CommonInput from "@/components/Common/Input";
import CommonSelect from "@/components/Common/Select";
import DialogDashboardTargetAmount from "@/components/DialogDashboardTargetAmount";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useIsAdmin,
  useIsFinancialManager,
} from "@/hooks/useIsFinancialManager";

type Props = {
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardProjectSummary({
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const isFinancial = useIsFinancialManager();
  const isAdmin = useIsAdmin();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<GetDashboardProjectSummaryContent>(
    DefaultDashbboardProjectSummary
  );
  const [targetAchievementList, setTargetAchievementList] = useState<
    ValueType[]
  >(DefaultDashboardProjectTargetAchievement);
  const [selectedTarget, setSelectedTarget] = useState<ValueType>(
    DefaultDashboardProjectTargetAchievement[0]
  );

  const showSmf = isFinancial || isAdmin;

  const getTargetAchievement = async () => {
    try {
      const result = await GetTargetAchievement();
      const targetItemList = result.data.pods[0].content.items.map((item) => ({
        label: item.year.toString(),
        value: item.target_achievement.toString(),
      }));
      if (targetItemList.length > 0) {
        setTargetAchievementList(targetItemList);
        setSelectedTarget(targetItemList[0]);
      }
    } catch (e) {
    } finally {
    }
  };
  const getSummary = async (newFilter: DashboardFilterType) => {
    try {
      setIsLoading(true);
      const params = getDashboardProjectFilterToString(newFilter);
      const resultList = await Promise.all([
        GetDashboardProjectSummary(params),
        GetTargetAchievement(),
      ]);

      const hasError = resultList.some((res) => !res.data.success);

      resultList.forEach((res) => {
        if (!res.data.success) {
          showToast({
            message: `프로젝트 요약 정보를 조회하는데 실패했습니다. ${res.data.error.type}`,
            type: "error",
          });
        }
      });

      if (!hasError) {
        const [summaryResult, targetResult] = resultList;

        const targetItemList = targetResult.data.pods[0].content.items.map(
          (item) => ({
            label: item.year.toString(),
            value: item.target_achievement.toString(),
          })
        );
        setSummary(summaryResult.data.pods[0].content);
        if (targetItemList.length > 0) {
          setTargetAchievementList(targetItemList);
          setSelectedTarget(targetItemList[0]);
        }
      }
    } catch (e) {
      showToast({
        message: `프로젝트 요약 정보를 조회하는데 실패했습니다. ${e}`,
        type: "error",
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  const handleInputExchangeRate = async (value: number) => {
    try {
      const result = await SetExchangeRate(value);
      if (result.data.success) {
        showToast({ message: "기준환율이 변경되었습니다.", type: "success" });
      } else {
        showToast({
          message: "기준환율 저장을 실패했습니다.",
          type: "error",
        });
      }
    } catch (e) {
      showToast({ message: "기준환율 저장을 실패했습니다.", type: "error" });
    } finally {
    }
  };

  const onLoad = () => {};

  useEffect(() => {
    if (searchReady) {
      getSummary(filter);
    }
  }, [searchReady]);

  useEffect(() => {
    onLoad();
  }, []);

  if (isLoading) {
    return (
      <div className="h-32 flex gap-5 justify-start items-start">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="flex-1 h-full bg-white rounded-xl p-5 flex flex-col justify-between"
          >
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-24" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="h-32 flex gap-5 justify-start items-start ">
      <DashboardSummaryCard
        title="계약금액"
        titleContent={
          <div className="min-w-16 flex gap-[4px] items-center text-right text-xs font-semibold text-gray-C">
            <span>기준환율</span>
            <CommonInput
              type="number"
              defaultValue={summary.base_exchange_rate}
              className="w-[45px] text-[10px] text-gray-A"
              onChange={(value) => {
                if (isNotEmpty(value)) {
                  setSummary((prev) => ({
                    ...prev,
                    base_exchange_rate: value,
                  }));
                }
              }}
              onKeyDownEnter={() => {
                handleInputExchangeRate(summary.base_exchange_rate);
              }}
              onBlur={() => {
                handleInputExchangeRate(summary.base_exchange_rate);
              }}
            />
            <span>원</span>
          </div>
        }
        content={`$${formatDollar(summary.total_contract_amount / summary.base_exchange_rate)}`}
      />
      <div className="flex-1 self-stretch p-5 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-D flex gap-5">
        <div className="w-full flex flex-col justify-between">
          <div className="text-gray-B text-sm font-semibold">TPP Margin</div>
          <div className="text-right text-4xl font-semibold">
            {formatToPercentString(summary.tpp_margin_rate, 2)}
          </div>
        </div>
        {showSmf && (
          <div className="w-full flex flex-col justify-between">
            <div className="text-gray-B text-sm font-semibold">
              자사제품 Margin
            </div>
            <div className="text-right text-4xl font-semibold">
              {formatToPercentString(summary.smf_margin_rate, 2)}
            </div>
          </div>
        )}
      </div>
      <DashboardSummaryCard
        title="계약률"
        content={formatToPercentString(summary.contract_rate, 1)}
      />
      <DashboardSummaryCard
        title="목표 달성률"
        titleContent={
          <div className="flex justify-start items-center gap-1">
            <div className="justify-center text-gray-B text-xs font-bold">
              목표 금액
            </div>
            <CommonSelect
              useAll={false}
              className="w-[70px]"
              defaultValue={selectedTarget.value}
              optionList={targetAchievementList}
              onChange={(_, option) => {
                setSelectedTarget(option);
              }}
            />
            <DialogDashboardTargetAmount
              dataList={targetAchievementList}
              onReload={getTargetAchievement}
            />
          </div>
        }
        content={
          selectedTarget.value === "0"
            ? "0%"
            : formatToPercentString(
                summary.total_contract_amount / Number(selectedTarget.value),
                1
              )
        }
      />
    </div>
  );
}
